import { For, Show, createEffect, createSignal, on } from "solid-js";

import { useLoginOut } from "@/api/clientApi/login.ts";
import {
  AnimationMenuSlide,
  useDropdownMenu,
} from "@/components/Base/AnimationMenuSlide/index.tsx";
import { DialogType } from "@/components/Dialogs/types.ts";
import { useDialog } from "@/components/Dialogs/useDialog.ts";
import Icon, { svgPathTypes } from "@/components/Icon";
import { useProfile } from "@/hooks/profile/useProfile.tsx";
import { useRouter } from "@/hooks/useRouter.tsx";
import { t } from "@/i18n";
import { getLocalizedPath } from "@/lib/language";
import { getHeadImgurl } from "@/lib/resFolder.ts";
import { findRouteInfo, normalizeRoutePath } from "@/lib/routeUtils";
import { layoutRoutes } from "@/router/routes";
import { userStore } from "@/stores/userStore.ts";

export default function UserDropdownMenu() {
  const { push, toDeposit, toWithdraw } = useRouter();
  const { openProfiles } = useProfile();
  const { showDialog } = useDialog();
  const [isPcOpen, setIsPcOpen] = createSignal(false);
  // 使用自定义 Hook 管理下拉菜单状态
  const {
    isOpen,
    isAnimating,
    toggleDropdown,
    startCloseAnimation,
    handleAnimationEnd,
  } = useDropdownMenu();

  const isValidRoute = () => {
    const normalPath = normalizeRoutePath(
      decodeURIComponent(location.pathname),
    );
    const routeInfo = findRouteInfo(
      layoutRoutes(),
      normalPath, // 处理目标路径：移除语言前缀、查询参数和开头的斜杠
    );
    // TODO
    const witherList = ["game/", "game-detail/"];
    const isWither = witherList.some((item) => normalPath.includes(item));
    if (isWither) {
      return true;
    }
    // 判断是否需要登录
    return routeInfo?.needLogin;
  };

  // Close dropdown when clicking outside
  const handleOutsideClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const dropdown = document.getElementById("user-dropdown");
    const trigger = document.getElementById("user-dropdown-trigger");

    if (
      dropdown &&
      trigger &&
      !dropdown.contains(target) &&
      !trigger.contains(target)
    ) {
      startCloseAnimation();
    }
  };

  const handleSignOut = () => {
    startCloseAnimation();
    showDialog({
      type: DialogType.Tips,
      data: {
        title: t("tips.sign_out"),
        content: t("tips.sign_out_content"),
        confirmText: t("tips.cancel"),
        cancelText: t("tips.sign_out"),
        onCancel: async () => {
          await useLoginOut();
          if (isValidRoute()) {
            location.href = getLocalizedPath("/");
          } else {
            location.reload();
          }
        },
      },
    });
  };

  // Add event listener for outside clicks
  createEffect(
    on(isOpen, () => {
      if (isOpen()) {
        document.addEventListener("click", handleOutsideClick);
      } else {
        document.removeEventListener("click", handleOutsideClick);
      }

      return () => {
        document.removeEventListener("click", handleOutsideClick);
      };
    }),
  );

  const menuItems: {
    icon: svgPathTypes;
    label: string;
    onClick: () => void;
  }[] = [
    {
      icon: "general/wallet",
      label: "wallet.title",
      onClick: () => {
        push("/wallet/balance");
        startCloseAnimation();
      },
    },
    {
      icon: "general/deposite2",
      label: "common.deposit1",
      onClick: () => {
        toDeposit();
        startCloseAnimation();
      },
    },
    {
      icon: "general/bottom_withdrawal",
      label: "common.withdraw",
      onClick: () => {
        toWithdraw();
        startCloseAnimation();
      },
    },
    {
      icon: "general/transaction",
      label: "account.transaction",
      onClick: () => {
        push("/wallet/transaction");
        startCloseAnimation();
      },
    },
    {
      icon: "general/bets_history",
      label: "account.history",
      onClick: () => {
        push("/wallet/betshistory");
        startCloseAnimation();
      },
    },
    {
      icon: "stats",
      label: "account.rollover",
      onClick: () => {
        showDialog({ type: DialogType.BillFlow });
        startCloseAnimation();
      },
    },
    {
      icon: "general/user",
      label: "account.profile",
      onClick: () => {
        openProfiles(userStore?.userInfo?.uid ?? 0);
        startCloseAnimation();
      },
    },
    {
      icon: "general/settings",
      label: "account.settings",
      onClick: () => {
        // navigate(getLocalizedPath("/settings?tab=account"));
        push("/settings?tab=account");
        startCloseAnimation();
      },
    },
  ];
  const closeDropdown = () => {
    if (isPcOpen()) {
      startCloseAnimation();
    } else {
      toggleDropdown();
    }
    setPcOpen();
  };
  const setPcOpen = () => setIsPcOpen(!isPcOpen());
  // 动画完成时重置
  const handleAnimationEndReset = () => {
    handleAnimationEnd();
    setIsPcOpen(false);
  };
  return (
    <div
      class="relative"
      // onMouseEnter={toggleDropdown}
      // onMouseLeave={startCloseAnimation}
    >
      {/* Trigger */}
      <div
        id="user-dropdown-trigger"
        class="flex h-10 cursor-pointer items-center gap-2 rounded-[var(--md)] bg-[var(--colors-surface-bg-8)] p-1 pl-3"
        onClick={closeDropdown}
      >
        <div
          class="size-6 shrink-0 rounded-[6px] bg-[var(--colors-surface-bg-9)] bg-contain bg-center"
          style={{
            "background-image": `url(${getHeadImgurl(Number(userStore.userInfo?.headimg))})`,
          }}
        />
        <div class="max-w-[120px] truncate text-[14px] font-bold leading-[14px] text-[var(--colors-text-primary)]">
          {userStore.userInfo?.nickname}
        </div>
        <Icon
          name="angle-down"
          class="size-4 text-[var(--colors-icon-btn-default)]"
        />
      </div>
      {/* Dropdown content */}
      <Show when={isOpen() || isAnimating()} fallback={<></>}>
        <div
          id="user-dropdown"
          class="absolute right-0 top-[38px] z-50 w-[232px] px-[6px] pt-[18px]"
        >
          <AnimationMenuSlide
            state={isOpen()}
            onAnimationEnd={handleAnimationEndReset}
          >
            <div class="bg-general-bg2 rounded-[12px] px-[6px] pt-[6px]">
              <For each={menuItems}>
                {(item) => (
                  <div
                    class={`hover:bg-general-bg7 group flex h-[46px] cursor-pointer items-center gap-[14px] rounded-lg pl-[20px] pr-[10px]`}
                    onClick={item.onClick}
                  >
                    <Icon
                      name={item.icon}
                      class="text-general-icon group-hover:text-general-iconlight h-[18px] w-[18px]"
                    />

                    <span class="text-general-darkzi group-hover:text-general-light mt-[2px] text-[14px] font-[500] leading-[14px]">
                      {t(item.label)}
                    </span>
                  </div>
                )}
              </For>
              <div class="bg-general-bg2 h-[10px] w-full" />
              <div
                class="bg-general-tmd20 -ml-[6px] flex h-[54px] w-[calc(100%+12px)] cursor-pointer items-center gap-[14px] rounded-b-lg px-[20px]"
                onClick={handleSignOut}
              >
                <Icon
                  name="general/sign_out"
                  class="text-general-bright h-[18px] w-[18px]"
                />
                <span class="text-general-darkzi text-[14px] font-medium">
                  {t("auth.sign_out")}
                </span>
              </div>
            </div>
          </AnimationMenuSlide>
        </div>
      </Show>
    </div>
  );
}
